/**
 * Mountain Journey Map
 * An elegant, abstract visualization of the 63-day Hero's Journey
 * as a mountain climb from Base Camp to Summit.
 * 
 * Design: Minimal, calm, premium - inspired by Headspace/Calm aesthetics
 * - Soft gradients and subtle lines
 * - Glowing Summit Beacon that brightens with progress
 * - User marker showing current position
 * - Clickable completed days to revisit content
 */

import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Star, Lock, Check, Sparkles } from "lucide-react";

// Level zone colors - subtle, elegant gradients
const ZONE_COLORS = {
  1: {
    bg: "from-slate-800/40 to-indigo-900/30",
    path: "#6366f1",
    pathGlow: "rgba(99, 102, 241, 0.3)",
    label: "Base Camp",
    sublabel: "Awakening & Awareness"
  },
  2: {
    bg: "from-indigo-900/30 to-violet-900/30",
    path: "#8b5cf6",
    pathGlow: "rgba(139, 92, 246, 0.3)",
    label: "The Path",
    sublabel: "Courage & Transformation"
  },
  3: {
    bg: "from-violet-900/30 to-amber-900/20",
    path: "#f59e0b",
    pathGlow: "rgba(245, 158, 11, 0.3)",
    label: "The Summit",
    sublabel: "Expansion & Flow"
  }
};

// Mythic threshold labels for key milestones
const THRESHOLD_LABELS = {
  21: { name: "Threshold 1", title: "Awareness" },
  42: { name: "Threshold 2", title: "Transformation" },
  63: { name: "Threshold 3", title: "Illumination" }
};

// Generate path points for a smooth mountain climb
const generatePathPoints = (containerHeight = 600) => {
  const points = [];
  const totalDays = 63;
  const dayHeight = containerHeight / totalDays;
  
  // Start from bottom (day 1) to top (day 63)
  for (let i = 0; i <= totalDays; i++) {
    const day = i;
    const y = containerHeight - (day * dayHeight);
    
    // Create gentle S-curve for the path
    // More horizontal movement at lower levels, straightens toward summit
    const level = day <= 21 ? 1 : day <= 42 ? 2 : 3;
    const progress = day / totalDays;
    
    // Wider oscillation at base, narrower at summit
    const amplitude = 60 * (1 - progress * 0.5);
    const frequency = 0.15;
    const x = 50 + Math.sin(day * frequency) * amplitude;
    
    points.push({ x, y, day, level });
  }
  
  return points;
};

// Summit Beacon Component - The glowing light at the top
const SummitBeacon = ({ progress, isComplete }) => {
  // Beacon intensity increases from 20% (start) to 100% (day 63)
  const intensity = 0.2 + (progress / 100) * 0.8;
  const glowSize = 40 + (progress / 100) * 60;
  
  return (
    <motion.div
      className="absolute top-0 left-1/2 -translate-x-1/2 z-20"
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1, delay: 0.5 }}
    >
      {/* Outer glow rings */}
      <motion.div
        className="absolute inset-0 rounded-full"
        style={{
          width: glowSize * 3,
          height: glowSize * 3,
          marginLeft: -(glowSize * 3) / 2 + 20,
          marginTop: -(glowSize * 3) / 2 + 20,
          background: `radial-gradient(circle, rgba(251, 191, 36, ${intensity * 0.15}) 0%, transparent 70%)`,
        }}
        animate={isComplete ? {
          scale: [1, 1.5, 1],
          opacity: [intensity * 0.15, intensity * 0.4, intensity * 0.15],
        } : {
          opacity: [intensity * 0.1, intensity * 0.2, intensity * 0.1],
        }}
        transition={{ duration: isComplete ? 2 : 4, repeat: Infinity, ease: "easeInOut" }}
      />
      
      {/* Middle glow */}
      <motion.div
        className="absolute inset-0 rounded-full"
        style={{
          width: glowSize * 2,
          height: glowSize * 2,
          marginLeft: -(glowSize * 2) / 2 + 20,
          marginTop: -(glowSize * 2) / 2 + 20,
          background: `radial-gradient(circle, rgba(251, 191, 36, ${intensity * 0.3}) 0%, transparent 60%)`,
        }}
        animate={isComplete ? {
          scale: [1, 1.3, 1],
        } : {}}
        transition={{ duration: 1.5, repeat: Infinity }}
      />
      
      {/* Core beacon */}
      <motion.div
        className="relative w-10 h-10 rounded-full flex items-center justify-center"
        style={{
          background: `radial-gradient(circle, rgba(251, 191, 36, ${intensity}) 0%, rgba(245, 158, 11, ${intensity * 0.8}) 50%, transparent 100%)`,
          boxShadow: `0 0 ${glowSize}px rgba(251, 191, 36, ${intensity * 0.6})`,
        }}
        animate={isComplete ? {
          scale: [1, 1.2, 1],
          boxShadow: [
            `0 0 ${glowSize}px rgba(251, 191, 36, ${intensity * 0.6})`,
            `0 0 ${glowSize * 2}px rgba(251, 191, 36, ${intensity})`,
            `0 0 ${glowSize}px rgba(251, 191, 36, ${intensity * 0.6})`,
          ],
        } : {
          boxShadow: [
            `0 0 ${glowSize * 0.8}px rgba(251, 191, 36, ${intensity * 0.4})`,
            `0 0 ${glowSize}px rgba(251, 191, 36, ${intensity * 0.6})`,
            `0 0 ${glowSize * 0.8}px rgba(251, 191, 36, ${intensity * 0.4})`,
          ],
        }}
        transition={{ duration: isComplete ? 1 : 3, repeat: Infinity, ease: "easeInOut" }}
      >
        {isComplete ? (
          <Sparkles size={20} className="text-amber-100" />
        ) : (
          <Star size={16} className="text-amber-200" style={{ opacity: intensity }} />
        )}
      </motion.div>
      
      {/* Summit label */}
      <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap">
        <p className="text-xs text-amber-400/80 font-medium tracking-wider uppercase">
          Summit
        </p>
      </div>
    </motion.div>
  );
};

// Day Marker on the path
const DayMarker = ({ 
  point, 
  isCompleted, 
  isCurrent, 
  isLocked, 
  onClick, 
  dayTitle,
  displayDay,
  level 
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  
  // Only show markers for milestone days (every 7th day) or current day
  const isMilestone = point.day % 7 === 0 || point.day === 21 || point.day === 42 || point.day === 63;
  const shouldShowMarker = isMilestone || isCurrent;
  
  if (!shouldShowMarker && !isCompleted) return null;
  
  const markerSize = isCurrent ? 28 : isMilestone ? 24 : 16;
  
  return (
    <motion.div
      className="absolute cursor-pointer z-10"
      style={{
        left: `${point.x}%`,
        top: point.y,
        transform: 'translate(-50%, -50%)',
      }}
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ delay: point.day * 0.02, duration: 0.3 }}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      onClick={() => !isLocked && onClick && onClick()}
    >
      {/* Marker circle */}
      <motion.div
        className={`rounded-full flex items-center justify-center transition-all ${
          isCurrent 
            ? 'bg-gradient-to-br from-amber-400 to-orange-500 shadow-lg shadow-amber-500/40' 
            : isCompleted 
              ? 'bg-emerald-500 shadow-md shadow-emerald-500/30' 
              : isLocked
                ? 'bg-white/10 border border-white/20'
                : 'bg-white/20 border border-white/30'
        }`}
        style={{ width: markerSize, height: markerSize }}
        whileHover={!isLocked ? { scale: 1.2 } : {}}
        whileTap={!isLocked ? { scale: 0.95 } : {}}
        animate={isCurrent ? {
          boxShadow: [
            '0 0 10px rgba(251, 191, 36, 0.4)',
            '0 0 20px rgba(251, 191, 36, 0.6)',
            '0 0 10px rgba(251, 191, 36, 0.4)',
          ]
        } : {}}
        transition={{ duration: 2, repeat: Infinity }}
      >
        {isCompleted ? (
          <Check size={markerSize * 0.5} className="text-white" />
        ) : isCurrent ? (
          <MapPin size={markerSize * 0.5} className="text-white" />
        ) : isLocked ? (
          <Lock size={markerSize * 0.4} className="text-white/40" />
        ) : (
          <span className="text-[10px] font-semibold text-white/70">{displayDay}</span>
        )}
      </motion.div>
      
      {/* Day number label for milestones - Now with mythic threshold names */}
      {isMilestone && (
        <div className={`absolute -right-10 top-1/2 -translate-y-1/2 text-right ${
          point.day === 21 || point.day === 42 || point.day === 63 ? 'font-semibold' : ''
        }`}>
          {THRESHOLD_LABELS[point.day] ? (
            <div className="flex flex-col items-end">
              <span className={`text-[8px] uppercase tracking-wider ${
                isCompleted ? 'text-emerald-400/70' : isCurrent ? 'text-amber-400/70' : 'text-white/30'
              }`}>
                {THRESHOLD_LABELS[point.day].name}
              </span>
              <span className={`text-[10px] ${
                isCompleted ? 'text-emerald-400' : isCurrent ? 'text-amber-400' : 'text-white/50'
              }`}>
                {THRESHOLD_LABELS[point.day].title}
              </span>
            </div>
          ) : (
            <span className={`text-[10px] ${
              isCompleted ? 'text-emerald-400' : isCurrent ? 'text-amber-400' : 'text-white/40'
            }`}>
              {`D${point.day}`}
            </span>
          )}
        </div>
      )}
      
      {/* Tooltip */}
      <AnimatePresence>
        {showTooltip && !isLocked && (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
            className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 rounded-lg bg-gray-900/95 backdrop-blur-sm border border-white/10 whitespace-nowrap z-50"
          >
            <p className="text-xs font-medium text-white">Day {point.day}</p>
            {dayTitle && (
              <p className="text-[10px] text-white/60 mt-0.5">{dayTitle}</p>
            )}
            {isCompleted && (
              <p className="text-[10px] text-emerald-400 mt-1">Tap to revisit</p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// User Position Indicator - "Hero Energy" - Animated marker showing current position
// Enhanced with more alive animations and energy effects
const UserPositionIndicator = ({ point, level, progress = 0 }) => {
  if (!point) return null;
  
  const levelColors = {
    1: 'from-indigo-400 to-violet-500',
    2: 'from-violet-400 to-fuchsia-500',
    3: 'from-amber-400 to-orange-500'
  };
  
  const levelGlows = {
    1: 'rgba(99, 102, 241, 0.6)',
    2: 'rgba(139, 92, 246, 0.6)',
    3: 'rgba(245, 158, 11, 0.6)'
  };
  
  // Intensity increases as user progresses through the level
  const levelProgress = level === 1 ? progress : level === 2 ? progress - 33 : progress - 66;
  const normalizedProgress = Math.max(0, Math.min(levelProgress / 33, 1));
  const energyIntensity = 0.5 + normalizedProgress * 0.5;
  
  return (
    <motion.div
      className="absolute z-30 pointer-events-none"
      style={{
        left: `${point.x}%`,
        top: point.y,
        transform: 'translate(-50%, -50%)',
      }}
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ type: "spring", duration: 0.8 }}
    >
      {/* Outer energy field - breathes with progress */}
      <motion.div
        className={`absolute rounded-full bg-gradient-to-br ${levelColors[level]}`}
        style={{ 
          width: 56 + (normalizedProgress * 16), 
          height: 56 + (normalizedProgress * 16), 
          marginLeft: -(28 + normalizedProgress * 8 - 12), 
          marginTop: -(28 + normalizedProgress * 8 - 12),
          opacity: 0.1
        }}
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.1, 0.2 * energyIntensity, 0.1],
        }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
      />
      
      {/* Primary pulse ring */}
      <motion.div
        className={`absolute inset-0 rounded-full bg-gradient-to-br ${levelColors[level]}`}
        style={{ width: 48, height: 48, marginLeft: -12, marginTop: -12 }}
        animate={{
          scale: [1, 1.5, 1],
          opacity: [0.3 * energyIntensity, 0, 0.3 * energyIntensity],
        }}
        transition={{ duration: 2, repeat: Infinity }}
      />
      
      {/* Secondary pulse ring - offset timing */}
      <motion.div
        className={`absolute inset-0 rounded-full bg-gradient-to-br ${levelColors[level]}`}
        style={{ width: 40, height: 40, marginLeft: -8, marginTop: -8 }}
        animate={{
          scale: [1, 1.3, 1],
          opacity: [0.2 * energyIntensity, 0, 0.2 * energyIntensity],
        }}
        transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
      />
      
      {/* Inner glow - intensifies with progress */}
      <motion.div
        className={`absolute rounded-full bg-gradient-to-br ${levelColors[level]}`}
        style={{ 
          width: 36, 
          height: 36, 
          marginLeft: -6, 
          marginTop: -6,
          filter: `blur(${8 + normalizedProgress * 4}px)`,
          opacity: 0.3 + normalizedProgress * 0.3
        }}
        animate={{
          opacity: [0.3 + normalizedProgress * 0.2, 0.5 + normalizedProgress * 0.3, 0.3 + normalizedProgress * 0.2],
        }}
        transition={{ duration: 1.5, repeat: Infinity }}
      />
      
      {/* Main marker - alive with gentle float */}
      <motion.div
        className={`relative w-7 h-7 rounded-full bg-gradient-to-br ${levelColors[level]} flex items-center justify-center`}
        style={{
          boxShadow: `0 0 ${12 + normalizedProgress * 10}px ${levelGlows[level]}`
        }}
        animate={{
          y: [0, -4, 0],
          boxShadow: [
            `0 0 ${10 + normalizedProgress * 8}px ${levelGlows[level]}`,
            `0 0 ${20 + normalizedProgress * 15}px ${levelGlows[level]}`,
            `0 0 ${10 + normalizedProgress * 8}px ${levelGlows[level]}`
          ]
        }}
        transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
      >
        <MapPin size={14} className="text-white" />
      </motion.div>
      
      {/* Energy particles - appear at higher progress */}
      {normalizedProgress > 0.3 && (
        <>
          {[...Array(Math.floor(normalizedProgress * 4))].map((_, i) => (
            <motion.div
              key={i}
              className={`absolute w-1 h-1 rounded-full bg-gradient-to-br ${levelColors[level]}`}
              style={{
                marginLeft: 10,
                marginTop: 10,
              }}
              animate={{
                x: [0, Math.cos(i * 1.5) * 20, 0],
                y: [0, -20 - Math.sin(i * 1.5) * 15, 0],
                opacity: [0, 0.8, 0],
                scale: [0.5, 1, 0.5]
              }}
              transition={{ 
                duration: 2 + i * 0.3, 
                repeat: Infinity, 
                delay: i * 0.4,
                ease: "easeOut"
              }}
            />
          ))}
        </>
      )}
    </motion.div>
  );
};

// Zone Label Component
const ZoneLabel = ({ zone, isActive, y }) => {
  const colors = ZONE_COLORS[zone];
  
  return (
    <motion.div
      className="absolute left-4 z-10"
      style={{ top: y }}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: zone * 0.2 }}
    >
      <div className={`px-3 py-1.5 rounded-lg backdrop-blur-sm ${
        isActive 
          ? 'bg-white/10 border border-white/20' 
          : 'bg-white/5 border border-white/10'
      }`}>
        <p className={`text-[10px] font-semibold tracking-wider uppercase ${
          isActive ? 'text-white' : 'text-white/50'
        }`}>
          {colors.label}
        </p>
        <p className={`text-[9px] ${isActive ? 'text-white/60' : 'text-white/30'}`}>
          {colors.sublabel}
        </p>
      </div>
    </motion.div>
  );
};

// Main Mountain Journey Map Component
const MountainJourneyMap = ({
  progress,
  journey,
  onDayClick,
  containerHeight = 500
}) => {
  const currentDay = progress?.current_day || 1;
  const completedDays = progress?.completed_days || [];
  const currentLevel = progress?.current_level || 1;
  const totalProgress = (completedDays.length / 63) * 100;
  const isJourneyComplete = completedDays.length === 63;
  
  // Generate path points
  const pathPoints = useMemo(() => generatePathPoints(containerHeight), [containerHeight]);
  
  // Find user's current position
  const userPoint = pathPoints.find(p => p.day === currentDay);
  
  // Generate SVG path string
  const pathString = useMemo(() => {
    if (pathPoints.length < 2) return '';
    
    let d = `M ${pathPoints[0].x} ${pathPoints[0].y}`;
    
    for (let i = 1; i < pathPoints.length; i++) {
      const prev = pathPoints[i - 1];
      const curr = pathPoints[i];
      
      // Smooth curve using quadratic bezier
      const cpX = (prev.x + curr.x) / 2;
      const cpY = (prev.y + curr.y) / 2;
      
      d += ` Q ${prev.x} ${prev.y} ${cpX} ${cpY}`;
    }
    
    return d;
  }, [pathPoints]);
  
  // Calculate completed path length percentage
  const completedPathPercent = (completedDays.length / 63) * 100;
  
  return (
    <div 
      className="relative w-full overflow-hidden rounded-2xl bg-gradient-to-b from-[#0a0a1a] via-[#0f0f25] to-[#0a0a1a]"
      style={{ height: containerHeight + 80 }}
    >
      {/* Background gradient zones */}
      <div className="absolute inset-0">
        {/* Level 1 zone - bottom */}
        <div 
          className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t ${ZONE_COLORS[1].bg}`}
          style={{ height: '33%' }}
        />
        {/* Level 2 zone - middle */}
        <div 
          className={`absolute left-0 right-0 bg-gradient-to-t ${ZONE_COLORS[2].bg}`}
          style={{ height: '33%', bottom: '33%' }}
        />
        {/* Level 3 zone - top */}
        <div 
          className={`absolute top-0 left-0 right-0 bg-gradient-to-t ${ZONE_COLORS[3].bg}`}
          style={{ height: '33%' }}
        />
      </div>
      
      {/* Subtle star field */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-30">
        {[...Array(30)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-0.5 h-0.5 bg-white rounded-full"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
            }}
            animate={{
              opacity: [0.2, 0.6, 0.2],
            }}
            transition={{
              duration: 2 + Math.random() * 3,
              repeat: Infinity,
              delay: Math.random() * 2
            }}
          />
        ))}
      </div>
      
      {/* Summit Beacon */}
      <SummitBeacon progress={totalProgress} isComplete={isJourneyComplete} />
      
      {/* Zone Labels */}
      <ZoneLabel zone={1} isActive={currentLevel === 1} y={containerHeight * 0.75} />
      <ZoneLabel zone={2} isActive={currentLevel === 2} y={containerHeight * 0.42} />
      <ZoneLabel zone={3} isActive={currentLevel === 3} y={containerHeight * 0.08} />
      
      {/* SVG Path */}
      <svg 
        className="absolute inset-0 w-full h-full" 
        style={{ top: 40 }}
        viewBox={`0 0 100 ${containerHeight}`}
        preserveAspectRatio="none"
      >
        {/* Path glow - subtle background */}
        <defs>
          <linearGradient id="pathGradient" x1="0%" y1="100%" x2="0%" y2="0%">
            <stop offset="0%" stopColor={ZONE_COLORS[1].path} stopOpacity="0.3" />
            <stop offset="33%" stopColor={ZONE_COLORS[2].path} stopOpacity="0.3" />
            <stop offset="100%" stopColor={ZONE_COLORS[3].path} stopOpacity="0.3" />
          </linearGradient>
          <linearGradient id="completedGradient" x1="0%" y1="100%" x2="0%" y2="0%">
            <stop offset="0%" stopColor="#10b981" stopOpacity="0.8" />
            <stop offset="50%" stopColor="#10b981" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#fbbf24" stopOpacity="0.9" />
          </linearGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        
        {/* Background path - subtle line showing the full journey */}
        <motion.path
          d={pathString}
          fill="none"
          stroke="url(#pathGradient)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray="4 8"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 2, ease: "easeOut" }}
        />
        
        {/* Completed path - solid bright line */}
        <motion.path
          d={pathString}
          fill="none"
          stroke="url(#completedGradient)"
          strokeWidth="3"
          strokeLinecap="round"
          filter="url(#glow)"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: completedPathPercent / 100 }}
          transition={{ duration: 1.5, ease: "easeOut", delay: 0.5 }}
        />
      </svg>
      
      {/* Day Markers */}
      <div className="absolute inset-0" style={{ top: 40 }}>
        {pathPoints.filter(p => p.day > 0).map((point) => {
          const dayData = journey?.days?.find(d => d.day === point.day);
          const isCompleted = completedDays.includes(point.day);
          const isCurrent = point.day === currentDay;
          const isLocked = point.day > currentDay;
          
          // Calculate display day (1-21 within each level)
          const levelStart = point.level === 1 ? 1 : point.level === 2 ? 22 : 43;
          const displayDay = point.day - levelStart + 1;
          
          return (
            <DayMarker
              key={point.day}
              point={point}
              isCompleted={isCompleted}
              isCurrent={isCurrent}
              isLocked={isLocked}
              displayDay={displayDay}
              dayTitle={dayData?.title}
              level={point.level}
              onClick={() => onDayClick && onDayClick(point.day, dayData)}
            />
          );
        })}
      </div>
      
      {/* User Position Indicator */}
      {userPoint && (
        <div className="absolute inset-0" style={{ top: 40 }}>
          <UserPositionIndicator point={userPoint} level={currentLevel} progress={totalProgress} />
        </div>
      )}
      
      {/* Progress Stats Bar */}
      <div className="absolute bottom-4 left-4 right-4 z-20">
        <div className="flex items-center justify-between px-4 py-3 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10">
          <div className="flex items-center gap-3">
            <div className="text-center">
              <p className="text-lg font-bold text-white">{completedDays.length}</p>
              <p className="text-[10px] text-white/50 uppercase tracking-wider">Days</p>
            </div>
            <div className="w-px h-8 bg-white/10" />
            <div className="text-center">
              <p className="text-lg font-bold text-amber-400">L{currentLevel}</p>
              <p className="text-[10px] text-white/50 uppercase tracking-wider">Level</p>
            </div>
          </div>
          <div className="flex-1 mx-4">
            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
              <motion.div
                className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-amber-400"
                initial={{ width: 0 }}
                animate={{ width: `${totalProgress}%` }}
                transition={{ duration: 1, delay: 0.5 }}
              />
            </div>
            <p className="text-[10px] text-white/40 text-center mt-1">
              {Math.round(totalProgress)}% Complete
            </p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-emerald-400">{63 - completedDays.length}</p>
            <p className="text-[10px] text-white/50 uppercase tracking-wider">To Go</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MountainJourneyMap;
